import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, MapPinned } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  generateSitePresenceWordDocument,
  PRESENCE_LEVEL_LABELS,
} from "@/lib/documentGenerator/basuWordGenerators";

interface Plan {
  id: string;
  handler_name: string | null;
  handler_contact: string | null;
  presence_level: string;
  presence_description: string | null;
  agreed_with: string | null;
  agreed_at: string | null;
  contact_person_name: string | null;
  contact_person_contact: string | null;
  out_of_hours_routine: string | null;
  escalation_routine: string | null;
  followup_note: string | null;
}

export const SitePresence: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data }, { data: proj }] = await Promise.all([
      supabase
        .from("site_presence_plans")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    setProjectName(proj?.name ?? "");
    setPlan((data as unknown as Plan) || null);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    setBusy(true);
    const { data, error } = await supabase
      .from("site_presence_plans")
      .insert({
        project_id: projectId,
        out_of_hours_routine:
          "Arbete utanför ordinarie arbetstid ska anmälas till Bas-U minst 24 timmar i förväg och godkännas skriftligt.",
        escalation_routine:
          "Vid allvarlig risk avbryts arbetet omedelbart. Bas-U kontaktas, därefter byggherrens kontaktperson.",
      })
      .select("*")
      .single();
    setBusy(false);
    if (error || !data) return toast.error("Kunde inte skapa dokumentet");
    setPlan(data as unknown as Plan);
  };

  const update = async (patch: Partial<Plan>) => {
    if (!plan) return;
    setPlan({ ...plan, ...patch });
    const { error } = await supabase.from("site_presence_plans").update(patch).eq("id", plan.id);
    if (error) toast.error("Kunde inte spara");
  };

  const exportWord = async () => {
    if (!plan) return;
    try {
      await generateSitePresenceWordDocument(plan, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="43"
        exportHistory={{ documentType: "platsnarvaro" }}
        title="Platsnärvaro Bas-U"
        description="Överenskommelse med byggherren om hur ofta Bas-U är på plats, vem som är kontaktperson och vad som gäller utanför ordinarie tid."
        legalRef="AFS 2023:3 kap. 2 § 17 · kap. 4"
        icon={MapPinned}
        actions={
          plan ? (
            <Button size="sm" variant="outline" onClick={exportWord}>
              <Download className="mr-1 h-4 w-4" /> Word
            </Button>
          ) : null
        }
      />

      {!plan ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <MapPinned className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Närvaron är inte dokumenterad. Utan en överenskommelse blir det oklart vem som ansvarar
              för samordningen när Bas-U inte är på plats.
            </p>
            <Button onClick={create} disabled={busy}>
              {busy ? "Skapar..." : "Dokumentera platsnärvaro"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Handläggare Bas-U</Label>
                <Input
                  defaultValue={plan.handler_name ?? ""}
                  onBlur={(e) => update({ handler_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Kontaktuppgifter</Label>
                <Input
                  defaultValue={plan.handler_contact ?? ""}
                  onBlur={(e) => update({ handler_contact: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Omfattning av närvaro</Label>
                <Select
                  value={plan.presence_level}
                  onValueChange={(v) => update({ presence_level: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRESENCE_LEVEL_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Överenskommet med (byggherre)</Label>
                <Input
                  defaultValue={plan.agreed_with ?? ""}
                  onBlur={(e) => update({ agreed_with: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Datum för överenskommelse</Label>
                <Input
                  type="date"
                  defaultValue={plan.agreed_at ?? ""}
                  onBlur={(e) => update({ agreed_at: e.target.value || null })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Beskrivning av närvaron</Label>
                <Textarea
                  rows={3}
                  placeholder="Till exempel: två heldagar per vecka samt vid alla riskfyllda moment."
                  defaultValue={plan.presence_description ?? ""}
                  onBlur={(e) => update({ presence_description: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Kontaktperson på arbetsplatsen</Label>
                <Input
                  defaultValue={plan.contact_person_name ?? ""}
                  onBlur={(e) => update({ contact_person_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Kontaktuppgifter</Label>
                <Input
                  defaultValue={plan.contact_person_contact ?? ""}
                  onBlur={(e) => update({ contact_person_contact: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Arbete utanför ordinarie tid</Label>
                <Textarea
                  rows={3}
                  defaultValue={plan.out_of_hours_routine ?? ""}
                  onBlur={(e) => update({ out_of_hours_routine: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Eskalering vid allvarlig risk</Label>
                <Textarea
                  rows={3}
                  defaultValue={plan.escalation_routine ?? ""}
                  onBlur={(e) => update({ escalation_routine: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Uppföljning</Label>
                <Textarea
                  rows={2}
                  defaultValue={plan.followup_note ?? ""}
                  onBlur={(e) => update({ followup_note: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SitePresence;
